import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { ROLE_PERMISSIONS, type StaffPermission } from '@/types/permissions'
import type { StaffRole } from '@/types/staff'

/**
 * Portal RBAC context — the client-side half of Contract C1's staff RBAC
 * seam. Drives route-guard + UI gating only (see the §16.1 note in
 * `types/permissions.ts`); the server re-checks every permission
 * independently via `staffauth.RequireStaffPermission`.
 */
export const useRbacStore = defineStore('rbac', () => {
  const role = ref<StaffRole | null>(null)

  // Derived client-side from `role` for now. Once staffauth ships, prefer a
  // server-issued permission list over client derivation so a role's grants
  // can change without a frontend redeploy.
  const permissions = computed<StaffPermission[]>(() =>
    role.value ? ROLE_PERMISSIONS[role.value] : [],
  )

  function hasPermission(permission: StaffPermission): boolean {
    return permissions.value.includes(permission)
  }

  function hasAllPermissions(required: readonly StaffPermission[]): boolean {
    return required.every((permission) => hasPermission(permission))
  }

  function setRole(next: StaffRole) {
    role.value = next
  }

  function clear() {
    role.value = null
  }

  return { role, permissions, hasPermission, hasAllPermissions, setRole, clear }
})
